import React, { useState, useEffect, useRef } from 'react';
import { Map as MapLibreMap, NavigationControl, Marker as MlMarker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import L from 'leaflet';
import apiClient from '../utils/apiClient';
import SurfConditionsDashboard from './SurfConditionsDashboard';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom beach marker icon
const beachIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="15" fill="#FF7F50" stroke="#fff" stroke-width="2"/>
      <circle cx="16" cy="12" r="3" fill="white"/>
      <path d="M8 24 Q16 18 24 24" stroke="white" stroke-width="2" fill="none"/>
      <text x="16" y="28" text-anchor="middle" fill="white" font-family="Arial" font-size="8" font-weight="bold">BEACH</text>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const PublicBeachSchedule = () => {
  const [assignments, setAssignments] = useState({});
  const [beaches, setBeaches] = useState([]);
  const [beachCoordinates, setBeachCoordinates] = useState([]);
  const [mapConfig, setMapConfig] = useState(null);
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [stormClosure, setStormClosure] = useState(null);
  const [stormClosureLoading, setStormClosureLoading] = useState(true);
  const publicMapRef = useRef(null);
  const publicMapContainerRef = useRef(null);

  const today = (() => {
    const todayDate = new Date();
    const year = todayDate.getFullYear();
    const month = String(todayDate.getMonth() + 1).padStart(2, '0');
    const day = String(todayDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  })();

  // Function to scroll to beach schedule section
  const scrollToBeaches = () => {
    const beachesSection = document.querySelector('.schedule-intro');
    if (beachesSection) {
      beachesSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Handle scroll to show/hide floating indicator
  useEffect(() => {
    const handleScroll = () => {
      const beachesSection = document.querySelector('.schedule-intro');
      if (beachesSection) {
        const rect = beachesSection.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight;
        setShowScrollIndicator(!isVisible);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Fetch weather data from internal API with caching (30-minute cache)
  const fetchWeatherData = async () => {
    try {
      console.log('🌤️ Fetching weather from internal API');
      
      const response = await apiClient.get('/api/public/weather', {
        timeout: 10000, // 10 second timeout
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        params: { _: Date.now() }
      });
      
      console.log('Weather data received:', response.data);
      
      setWeather({
        temperature: response.data.temperature,
        humidity: response.data.humidity,
        windSpeed: response.data.windSpeed,
        uvIndex: response.data.uvIndex,
        visibility: response.data.visibility,
        conditions: response.data.conditions,
        cached: response.data.cached,
        cacheAge: response.data.cacheAge,
        fresh: response.data.fresh,
        stale: response.data.stale,
        default: response.data.default,
        error: response.data.error
      });
    } catch (error) {
      console.warn('Failed to fetch weather data from internal API:', error.message);
      
      // Set pleasant default weather data if API fails
      setWeather({
        temperature: 75,
        humidity: 65,
        windSpeed: 8,
        uvIndex: 6,
        visibility: 10,
        conditions: 'Partly Cloudy',
        error: true
      });
    }
  };

  // Convert weather codes to descriptions
  const getWeatherDescription = (code) => {
    const codes = {
      1000: 'Clear',
      1100: 'Mostly Clear',
      1101: 'Partly Cloudy',
      1102: 'Mostly Cloudy',
      1001: 'Cloudy',
      2000: 'Fog',
      2100: 'Light Fog',
      4000: 'Drizzle',
      4001: 'Rain',
      4200: 'Light Rain',
      4201: 'Heavy Rain',
      5000: 'Snow',
      5001: 'Flurries',
      5100: 'Light Snow',
      5101: 'Heavy Snow',
      6000: 'Freezing Drizzle',
      6001: 'Freezing Rain',
      6200: 'Light Freezing Rain',
      6201: 'Heavy Freezing Rain',
      7000: 'Ice Pellets',
      7101: 'Heavy Ice Pellets',
      7102: 'Light Ice Pellets',
      8000: 'Thunderstorm'
    };
    return codes[code] || 'Unknown';
  };

  // Fetch weather forecast data from internal API with caching (1-hour cache)
  const fetchForecastData = async () => {
    try {
      console.log('🌤️ Fetching forecast from internal API');
      
      const response = await apiClient.get('/api/public/weather/forecast', {
        timeout: 15000, // 15 second timeout
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        params: { _: Date.now() }
      });
      
      console.log('Forecast data received:', response.data);
      setForecast(response.data);
    } catch (error) {
      console.warn('Failed to fetch forecast data from internal API:', error.message);
      
      // Set default forecast data if API fails
      setForecast({
        hourly: [
          {
            time: new Date().toISOString(),
            values: {
              temperature: 22,
              humidity: 65,
              windSpeed: 3.5,
              uvIndex: 6,
              visibility: 16,
              weatherCode: 1101,
              precipitationProbability: 10
            }
          }
        ],
        daily: [
          {
            time: (() => {
              const date = new Date();
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}T00:00:00Z`;
            })(),
            values: {
              temperatureMax: 25,
              temperatureMin: 18,
              humidity: 65,
              windSpeed: 3.5,
              uvIndex: 6,
              visibility: 16,
              weatherCode: 1101,
              precipitationProbability: 10
            }
          }
        ],
        error: true
      });
    }
  };

  // Get weather icon based on weather code
  const getWeatherIcon = (code) => {
    const icons = {
      1000: '☀️', // Clear
      1100: '🌤️', // Mostly Clear
      1101: '⛅', // Partly Cloudy
      1102: '🌥️', // Mostly Cloudy
      1001: '☁️', // Cloudy
      2000: '🌫️', // Fog
      2100: '🌫️', // Light Fog
      4000: '🌦️', // Drizzle
      4001: '🌧️', // Rain
      4200: '🌦️', // Light Rain
      4201: '⛈️', // Heavy Rain
      5000: '❄️', // Snow
      5001: '🌨️', // Flurries
      5100: '🌨️', // Light Snow
      5101: '❄️', // Heavy Snow
      6000: '🌨️', // Freezing Drizzle
      6001: '🌨️', // Freezing Rain
      6200: '🌨️', // Light Freezing Rain
      6201: '🌨️', // Heavy Freezing Rain
      7000: '🌨️', // Ice Pellets
      7101: '🌨️', // Heavy Ice Pellets
      7102: '🌨️', // Light Ice Pellets
      8000: '⛈️'  // Thunderstorm
    };
    return icons[code] || '🌤️';
  };

  // Convert Celsius to Fahrenheit
  const celsiusToFahrenheit = (celsius) => {
    return Math.round(celsius * 9/5 + 32);
  };

  // Convert m/s to mph
  const msToMph = (ms) => {
    return Math.round(ms * 2.237);
  };

  // Format time for display
  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      hour12: true 
    });
  };

  // Format date for display
  const formatDate = (timeString) => {
    return new Date(timeString).toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Fetch storm closure status
  const fetchStormClosureStatus = async () => {
    try {
              const response = await apiClient.get('/api/public/storm-closure');
      setStormClosure(response.data);
      console.log('Storm closure status:', response.data);
    } catch (error) {
      console.warn('Failed to fetch storm closure status:', error.message);
      setStormClosure({ isActive: false, closure: null });
    } finally {
      setStormClosureLoading(false);
    }
  };

  // Fetch beach coordinates for map
  const fetchBeachCoordinates = async () => {
    try {
      console.log('🌍 Fetching beach coordinates for map');
      const response = await apiClient.get('/api/public/beaches/coordinates');
      setBeachCoordinates(response.data);
      console.log('Beach coordinates received:', response.data);
    } catch (error) {
      console.warn('Failed to fetch beach coordinates:', error.message);
      setBeachCoordinates([]);
    }
  };

  // Fetch map configuration
  const fetchMapConfig = async () => {
    try {
      console.log('⚙️ Fetching map configuration');
      const response = await apiClient.get('/api/public/map-config');
      setMapConfig(response.data);
      console.log('Map config received:', response.data);
    } catch (error) {
      console.warn('Failed to fetch map configuration:', error.message);
      setMapConfig({
        center_latitude: 39.7496,
        center_longitude: -74.1039,
        default_zoom: 13,
        rotation: 0
      });
    }
  };

  // Fetch beach assignments and beaches data together
  const fetchScheduleData = async () => {
    try {
              const response = await apiClient.get(`/api/public/schedule/${today}`);
      if (response.data.assignments && response.data.beaches) {
        setAssignments(response.data.assignments);
        setBeaches(response.data.beaches);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch schedule data:', error);
        setError('Unable to load today\'s schedule');
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchScheduleData(),
        fetchWeatherData(),
        fetchForecastData(),
        fetchStormClosureStatus(),
        fetchBeachCoordinates(),
        fetchMapConfig()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Initialize public maplibre after data loaded
  useEffect(() => {
    if (!publicMapContainerRef.current) return;
    if (!mapConfig || !beachCoordinates || beachCoordinates.length === 0) return;
    if (publicMapRef.current) return; // init once

    try {
      const map = new MapLibreMap({
        container: publicMapContainerRef.current,
        style: {
          version: 8,
          sources: {
            osm: {
              type: 'raster',
              tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
              tileSize: 256,
              attribution: '© OpenStreetMap contributors'
            }
          },
          layers: [{ id: 'osm', type: 'raster', source: 'osm' }]
        },
        center: [mapConfig.center_longitude, mapConfig.center_latitude],
        zoom: mapConfig.default_zoom,
        bearing: mapConfig.rotation || 0,
        pitch: 0
      });
      map.addControl(new NavigationControl({ visualizePitch: false }), 'top-left');
      publicMapRef.current = map;

      const addMarkers = () => {
        const isGuarded = (beachId) => {
          const assn = assignments && assignments[beachId];
          return Array.isArray(assn) && assn.length > 0;
        };
        beachCoordinates.forEach((b) => {
          if (!isGuarded(b.id)) return;
          if (Number.isFinite(Number(b.latitude)) && Number.isFinite(Number(b.longitude))) {
            const container = document.createElement('div');
            container.style.display = 'flex';
            container.style.flexDirection = 'column';
            container.style.alignItems = 'center';
            const img = document.createElement('img');
            img.src = '/stand.svg';
            img.alt = 'Beach';
            img.style.width = '22px';
            img.style.height = '22px';
            const color = (b.flag_status || 'black').toLowerCase();
            const colorMap = {
              black: 'brightness(0) saturate(100%)',
              green: 'invert(45%) sepia(97%) saturate(414%) hue-rotate(83deg) brightness(90%) contrast(95%)',
              yellow: 'invert(77%) sepia(86%) saturate(527%) hue-rotate(356deg) brightness(101%) contrast(101%)',
              red: 'invert(22%) sepia(85%) saturate(4059%) hue-rotate(346deg) brightness(85%) contrast(102%)'
            };
            img.style.filter = colorMap[color] || colorMap.black;
            const label = document.createElement('span');
            label.textContent = b.name || '';
            label.style.marginTop = '1px';
            label.style.fontSize = '11px';
            label.style.fontWeight = '600';
            label.style.color = '#1f2937';
            label.style.padding = '1px 4px';
            label.style.background = 'rgba(255,255,255,0.9)';
            label.style.border = '1px solid rgba(0,0,0,0.15)';
            label.style.borderRadius = '4px';
            container.appendChild(img);
            container.appendChild(label);
            new MlMarker({ element: container, anchor: 'bottom' })
              .setLngLat([Number(b.longitude), Number(b.latitude)])
              .addTo(map);
          }
        });
      };

      const handleLoad = () => {
        try { map.resize(); } catch (e) {}
        addMarkers();
      };
      map.on('load', handleLoad);
      setTimeout(() => { try { map.resize(); } catch (e) {} }, 0);
      setTimeout(() => { try { map.resize(); } catch (e) {} }, 250);
      window.addEventListener('resize', handleLoad);
    } catch (e) {
      console.error('Failed to initialize public map:', e);
    }
  }, [mapConfig, beachCoordinates]);

  

  // Filter beaches to show - beaches with 2+ people OR combined bay if both AM/PM have assignments
  const getDisplayBeaches = () => {
    if (!beaches.length || !Object.keys(assignments).length) return [];

    const bayAM = beaches.find(b => b.name.toLowerCase() === 'bay am');
    const bayPM = beaches.find(b => b.name.toLowerCase() === 'bay pm');
    const beach25th = beaches.find(b => b.name.toLowerCase().includes('25th'));
    
    const bayAMAssigned = bayAM && assignments[bayAM.id] && assignments[bayAM.id].length >= 1;
    const bayPMAssigned = bayPM && assignments[bayPM.id] && assignments[bayPM.id].length >= 1;
    const showCombinedBay = bayAMAssigned && bayPMAssigned;

    let displayBeaches = beaches.filter(beach => {
      const beachAssignments = assignments[beach.id] || [];
      
      // Skip individual bay beaches - we'll handle them separately
      if (beach.name.toLowerCase().includes('bay')) {
        return false;
      }
      
      // Show regular beaches with 2+ people
      return beachAssignments.length >= 2;
    });

    // Add combined Bay if both AM and PM have assignments
    if (showCombinedBay) {
      const combinedBayEmployees = [
        ...(assignments[bayAM.id] || []),
        ...(assignments[bayPM.id] || [])
      ];
      
      displayBeaches.push({
        id: 'combined-bay',
        name: 'Bay',
        description: 'Small Child Friendly Beach',
        sort_order: bayAM.sort_order,
        employees: combinedBayEmployees
      });
    }

    // Sort by sort_order
    displayBeaches.sort((a, b) => a.sort_order - b.sort_order);

    return displayBeaches;
  };

  // Get employee count for a beach, with special handling for 25th street and combined bay
  const getBeachEmployeeCount = (beach) => {
    if (beach.id === 'combined-bay') {
      return 1; // Always show 1 lifeguard for combined bay
    }
    
    let count = (assignments[beach.id] || []).length;
    
    // No special count adjustment needed - 25th street is now treated as normal beach
    
    return count;
  };

  // Get employees for a beach (for display purposes)
  const getBeachEmployees = (beach) => {
    if (beach.id === 'combined-bay') {
      return beach.employees;
    }
    return assignments[beach.id] || [];
  };

  const displayBeaches = getDisplayBeaches();

  if (loading) {
    return (
      <div className="public-schedule-loading">
        <div className="wave-animation">🌊</div>
        <p>Loading today's beach schedule...</p>
      </div>
    );
  }

  return (
    <div className="public-beach-schedule">
      {/* Header */}
      <header className="beach-header">
        <div className="header-content">
          <h1 className="beach-title">
            {process.env.REACT_APP_PUBLIC_HEADER || '🏖️ Beach Patrol'}
          </h1>
          <p className="header-date">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        
        {/* Weather Widget */}
        {weather && (
          <div className="weather-widget">
            <div className="weather-main">
              <span className="temperature">{weather.temperature}°F</span>
              <span className="conditions">{weather.conditions}</span>
            </div>
            <div className="weather-details">
              <div className="weather-item">
                <span className="weather-icon">💨</span>
                <span>{weather.windSpeed} mph</span>
              </div>
              <div className="weather-item">
                <span className="weather-icon">💧</span>
                <span>{weather.humidity}%</span>
              </div>
              <div className="weather-item">
                <span className="weather-icon">☀️</span>
                <span>UV {weather.uvIndex}</span>
              </div>
              <div className="weather-item">
                <span className="weather-icon">👁️</span>
                <span>{weather.visibility} mi</span>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="schedule-content">
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {stormClosure?.isActive ? (
          <>
            {/* Full-Screen Storm Closure Notification */}
            <div className="storm-closure-fullscreen">
              <div className="storm-background-fullscreen">
                <div className="lightning">⚡</div>
                <div className="lightning">⚡</div>
                <div className="lightning">⚡</div>
                <div className="lightning">⚡</div>
                <div className="lightning">⚡</div>
                <div className="storm-clouds-main">☁️⛈️☁️</div>
                <div className="rain-effect">
                  <div className="rain-drop"></div>
                  <div className="rain-drop"></div>
                  <div className="rain-drop"></div>
                  <div className="rain-drop"></div>
                  <div className="rain-drop"></div>
                </div>
              </div>

              <div className="closure-content-fullscreen">
                <div className="closure-icon-large">⛈️</div>
                <h1 className="closure-title-large">BEACHES CLOSED</h1>
                <div className="closure-subtitle">Due to Dangerous Weather Conditions</div>
                
                <div className="closure-reason-large">
                  <div className="reason-label">REASON:</div>
                  <div className="reason-text">{stormClosure.closure?.reason || 'Severe weather conditions'}</div>
                </div>

                <div className="safety-warning">
                  <div className="warning-icon">🚨</div>
                  <div className="warning-text">
                    <p><strong>FOR YOUR SAFETY:</strong></p>
                    <p>• Get off the beach immediately</p>
                    <p>• Seek shelter in a sturdy building</p>
                    <p>• Avoid water activities</p>
                    <p>• Monitor weather updates on this website</p>
                  </div>
                </div>

                <div className="closure-status">
                  <div className="status-item">
                    <span className="status-label">Closed at:</span>
                    <span className="status-value">
                      {new Date(stormClosure.closure?.closedAt).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </span>
                  </div>
                  {stormClosure.closure?.closedBy && (
                    <div className="status-item">
                      <span className="status-label">Authorized by:</span>
                      <span className="status-value">
                        {stormClosure.closure.closedBy.firstName} {stormClosure.closure.closedBy.lastName}
                      </span>
                    </div>
                  )}
                </div>

                <div className="reopening-notice">
                  <p>Beaches will reopen when weather conditions are safe</p>
                  <p>Check back for updates</p>
                </div>
              </div>

              {/* 5-Hour Weather Forecast Section - Positioned lower */}
              {forecast && (
                <div className="forecast-section-storm">
                  <div className="forecast-container-storm">
                    <h3>🌤️ 5-Hour Weather Forecast</h3>
                    <div className="hourly-forecast-storm">
                      {forecast.hourly && forecast.hourly.slice(0, 5).map((hour, index) => (
                        <div key={index} className="hour-card-storm">
                          <div className="hour-time">{formatTime(hour.time)}</div>
                          <div className="hour-icon">{getWeatherIcon(hour.values.weatherCode)}</div>
                          <div className="hour-temp">{celsiusToFahrenheit(hour.values.temperature)}°</div>
                          <div className="hour-details-storm">
                            <div className="hour-detail">
                              <span>💨</span>
                              <span>{msToMph(hour.values.windSpeed)} mph</span>
                            </div>
                            <div className="hour-detail">
                              <span>💧</span>
                              <span>{hour.values.precipitationProbability || 0}%</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )} 
            </div>
          </>
        ) : displayBeaches.length === 0 ? (
          <div className="no-schedule">
            <div className="beach-scene">
              <div className="sun">☀️</div>
              <div className="waves">🌊🌊🌊</div>
            </div>
            <h2>No Schedule Available</h2>
            <p>No lifeguard assignments are posted for today.</p>
          </div>
        ) : (
          <>
            {/* Beach Map Section */}
            {mapConfig && beachCoordinates.length > 0 && (
              <div className="beach-map-section">
                <div className="forecast-container">
                  <h2>🗺️ Beach Locations Map</h2>
                  <p>Find your favorite beach and see where our lifeguards are on duty, only beaches where life guards are working are shown.</p>
                  <div className="map-container" ref={publicMapContainerRef} style={{ height: '400px', width: '100%' }} />
                  {/* Legend / Key */}
                  <div style={{ marginTop: '10px' }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: '8px',
                      alignItems: 'center'
                    }}>
                      {[{
                        label: 'Black - Condition Not Set',
                        filter: 'brightness(0) saturate(100%)'
                      },{
                        label: 'Green - Conditions Calm',
                        filter: 'invert(45%) sepia(97%) saturate(414%) hue-rotate(83deg) brightness(90%) contrast(95%)'
                      },{
                        label: 'Yellow - Intermediate Conditions, Use Caution',
                        filter: 'invert(77%) sepia(86%) saturate(527%) hue-rotate(356deg) brightness(101%) contrast(101%)'
                      },{
                        label: 'Red - Rough Conditions, Extreme Caution against Swimming',
                        filter: 'invert(22%) sepia(85%) saturate(4059%) hue-rotate(346deg) brightness(85%) contrast(102%)'
                      }].map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <img src="/stand.svg" alt="legend icon" style={{ width: 16, height: 16, filter: item.filter }} />
                          <span style={{ fontSize: '12px', color: '#1f2937' }}>{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Surf Conditions Section (moved below beach map) */}
            <div className="surf-conditions-section">
              <SurfConditionsDashboard />
            </div>

            {/* 5-Hour Weather Forecast Section (moved below beach map) */}
            {forecast && (
              <div className="forecast-section">
                <div className="forecast-container">
                  <h2>🌤️ 5-Hour Forecast</h2>
                  <div className="hourly-forecast">
                    {forecast.hourly && forecast.hourly.slice(0, 5).map((hour, index) => (
                      <div key={index} className="hour-card">
                        <div className="hour-time">{formatTime(hour.time)}</div>
                        <div className="hour-icon">{getWeatherIcon(hour.values.weatherCode)}</div>
                        <div className="hour-temp">{celsiusToFahrenheit(hour.values.temperature)}°</div>
                        <div className="hour-details">
                          <div className="hour-detail">
                            <span>💨</span>
                            <span>{msToMph(hour.values.windSpeed)} mph</span>
                          </div>
                          <div className="hour-detail">
                            <span>💧</span>
                            <span>{hour.values.precipitationProbability || 0}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="schedule-intro">
              <h2>Today's Guarded Beaches</h2>
              <p>Our lifeguards are on duty at the following locations:</p>
            </div>

            <div className="beaches-grid">
              {displayBeaches.map(beach => {
                const beachEmployees = getBeachEmployees(beach);
                const employeeCount = getBeachEmployeeCount(beach);
                
                return (
                  <div key={beach.id} className="beach-card">
                    <div className="beach-card-header">
                      <h3 className="beach-name">
                        {beach.name.includes('Bay') ? '🏊‍♂️' : '🏖️'} {beach.name}
                      </h3>
                      {(/bay/i.test(beach.name)) && beach.description && (
                        <p className="beach-description">{beach.description}</p>
                      )}
                    </div>
                    
                    <div className="lifeguard-status">
                      <div className="status-indicator">
                        <span className="status-icon">🏊‍♂️</span>
                        <span className="status-text">Lifeguards on Duty</span>
                      </div>
                    </div>
                    
                    <div className="beach-card-footer">
                      <span className="staff-count">
                        {employeeCount} {employeeCount === 1 ? 'Lifeguard' : 'Lifeguards'} on duty
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 3-Day Forecast Section - Moved to Bottom */}
            {forecast && (
              <div className="forecast-section">
                <div className="forecast-container">
                  <h2>📅 3-Day Forecast</h2>
                  <div className="daily-forecast">
                    {forecast.daily && forecast.daily.slice(0, 3).map((day, index) => (
                      <div key={index} className="day-card">
                        <div className="day-header">
                          <div className="day-date">{formatDate(day.time)}</div>
                          <div className="day-icon">{getWeatherIcon(day.values.weatherCodeMax || day.values.weatherCode)}</div>
                        </div>
                        <div className="day-temps">
                          <span className="day-high">
                            {celsiusToFahrenheit(day.values.temperatureMax || day.values.temperature)}°
                          </span>
                          <span className="day-low">
                            {celsiusToFahrenheit(day.values.temperatureMin || day.values.temperature - 5)}°
                          </span>
                        </div>
                        <div className="day-condition">
                          {getWeatherDescription(day.values.weatherCodeMax || day.values.weatherCode)}
                        </div>
                        <div className="day-details">
                          <div className="day-detail">
                            <span>💨</span>
                            <span>{msToMph(day.values.windSpeedAvg || day.values.windSpeed)} mph</span>
                          </div>
                          <div className="day-detail">
                            <span>☀️</span>
                            <span>UV {day.values.uvIndexMax || day.values.uvIndex}</span>
                          </div>
                          <div className="day-detail">
                            <span>💧</span>
                            <span>{day.values.precipitationProbabilityAvg || day.values.precipitationProbability || 0}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Floating Scroll Indicator */}
        {showScrollIndicator && (
          <div className="floating-scroll-indicator" onClick={scrollToBeaches}>
            <div className="floating-scroll-text">See Guarded Beaches</div>
            <div className="floating-scroll-arrow">↓</div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="beach-footer">
        <div className="footer-content">
          <div className="safety-info">
            <h3>🚨 Beach Safety Reminders</h3>
            <ul>
              <li>Always swim near a lifeguard</li>
              <li>Obey all posted signs and flags</li>
              <li>Stay hydrated and use sunscreen</li>
              <li>Never swim alone</li>
            </ul>
          </div>
          <div className="emergency-info">
            <h3>🆘 Emergency</h3>
            <p>For emergencies, dial <strong>911</strong></p>
            <p>Or find the nearest lifeguard</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="copyright">
            <span>© {new Date().getFullYear()}</span>
            <a
              href="https://lighthouse.hudsonriggs.systems"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              <img src="/lighthouse_logo.svg" alt="LightHouse" className="footer-logo" />
              LightHouse
            </a>
            <span>— a</span>
            <a
              href="https://hudsonriggs.systems"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              <img src="/hrsys_logo.svg" alt="Hudson Riggs Systems" className="footer-logo" />
              Hudson Riggs Systems
            </a>
            <span>Product.</span>
          </p>
          <div className="wave-footer">🌊🌊🌊🌊🌊</div>
        </div>
      </footer>

      <style jsx="true">{`
        .public-beach-schedule {
          min-height: 100vh;
          background: linear-gradient(135deg, var(--public-bg-start, #87CEEB) 0%, var(--public-bg-mid, #98E4FF) 50%, var(--public-bg-end, #87CEEB) 100%);
          font-family: 'Arial', sans-serif;
        }

        .public-schedule-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: linear-gradient(135deg, var(--public-bg-start, #87CEEB) 0%, var(--public-bg-mid, #98E4FF) 50%, var(--public-bg-end, #87CEEB) 100%);
          color: var(--public-primary, #2C5F7F);
          font-size: 1.2rem;
        }

        .wave-animation {
          font-size: 3rem;
          animation: wave 2s ease-in-out infinite;
        }

        @keyframes wave {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        .beach-header {
          background: linear-gradient(45deg, var(--public-header-start, #FF7F50), var(--public-header-end, #FFB347));
          color: white;
          padding: 2rem;
          text-align: center;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          position: relative;
          overflow: hidden;
        }

        .beach-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 20"><path d="M0,10 Q25,0 50,10 T100,10 L100,20 L0,20 Z" fill="rgba(255,255,255,0.1)"/></svg>') repeat;
          background-size: 200px 60px;
          background-position: 0 0;
          will-change: background-position;
          animation: waves 12s linear infinite;
        }

        @keyframes waves {
          0% { background-position: 0 0; }
          100% { background-position: -200px 0; }
        }

        .beach-header {
          position: relative;
          min-height: 140px;
        }

        .header-content {
          position: relative;
          z-index: 2;
        }

        .beach-title {
          font-size: 3rem;
          margin: 0 0 0.5rem 0;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
        }

        .header-date {
          font-size: 1.2rem;
          opacity: 0.9;
          margin: 0;
        }

        .weather-widget {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(255, 255, 255, 0.95);
          color: var(--public-primary, #2C5F7F);
          padding: 1rem;
          border-radius: 15px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(10px);
        }

        .weather-main {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .temperature {
          font-size: 1.5rem;
          font-weight: bold;
          color: var(--public-orange-start, #FF7F50);
        }

        .conditions {
          font-size: 0.9rem;
        }

        .weather-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.25rem;
          font-size: 0.8rem;
        }

        .weather-item {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .weather-icon {
          font-size: 1rem;
        }

        .schedule-content {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .error-message {
          background: #FFE4E1;
          color: var(--danger-color, #DC143C);
          padding: 1rem;
          border-radius: 10px;
          text-align: center;
          margin-bottom: 2rem;
          border: 2px solid var(--danger-light, #FFB6C1);
        }

        .error-icon {
          font-size: 1.5rem;
          margin-right: 0.5rem;
        }

        .no-schedule {
          text-align: center;
          padding: 4rem 2rem;
          color: var(--public-primary, #2C5F7F);
        }

        .beach-scene {
          font-size: 4rem;
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .sun {
          animation: sun-glow 3s ease-in-out infinite alternate;
        }

        @keyframes sun-glow {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }

        .waves {
          animation: wave-motion 2s ease-in-out infinite;
        }

        @keyframes wave-motion {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(10px); }
        }

        .forecast-section {
          margin-bottom: 4rem;
        }

        .forecast-container {
          background: rgba(255, 255, 255, 0.9);
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
          border: 2px solid var(--public-border, #87CEEB);
        }

        .forecast-container h2 {
          color: var(--public-primary, #2C5F7F);
          text-align: center;
          margin-bottom: 1.5rem;
          font-size: 1.8rem;
          text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.8);
        }

        .hourly-forecast {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 1rem;
        }

        .hour-card {
          background: linear-gradient(135deg, var(--primary-light, #F0F8FF) 0%, var(--gray-100, #E6F3FF) 100%);
          border-radius: 15px;
          padding: 1rem;
          text-align: center;
          border: 2px solid var(--public-border, #B0E0E6);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .hour-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
        }

        .hour-time {
          font-size: 0.9rem;
          font-weight: bold;
          color: var(--public-primary, #2C5F7F);
          margin-bottom: 0.5rem;
        }

        .hour-icon {
          font-size: 2rem;
          margin-bottom: 0.5rem;
        }

        .hour-temp {
          font-size: 1.2rem;
          font-weight: bold;
          color: var(--public-orange-start, #FF7F50);
          margin-bottom: 0.5rem;
        }

        .hour-details {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .hour-detail {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.25rem;
          font-size: 0.8rem;
          color: var(--public-primary, #2C5F7F);
        }

        .daily-forecast {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .day-card {
          background: linear-gradient(135deg, var(--primary-light, #F0F8FF) 0%, var(--gray-100, #E6F3FF) 100%);
          border-radius: 20px;
          padding: 1.5rem;
          border: 2px solid var(--public-border, #B0E0E6);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .day-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
        }

        .day-header {
          background: #fff0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .upcoming-tides {
          background: #ffffff4a;
        }

        .day-date {
          font-size: 1.1rem;
          font-weight: bold;
          color: var(--public-primary, #2C5F7F);
        }

        .day-icon {
          font-size: 2.5rem;
        }

        .day-temps {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 0.5rem;
        }

        .day-high {
          font-size: 1.5rem;
          font-weight: bold;
          color: var(--public-orange-start, #FF7F50);
        }

        .day-low {
          font-size: 1.2rem;
          color: var(--public-accent, #20B2AA);
        }

        .day-condition {
          text-align: center;
          font-size: 1rem;
          color: var(--public-primary, #2C5F7F);
          margin-bottom: 1rem;
          font-weight: 500;
        }

        .day-details {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
        }

        .day-detail {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.8rem;
          color: #2C5F7F;
        }

        .surf-conditions-section {
          margin-bottom: 3rem;
        }

        .beach-map-section {
          margin-bottom: 3rem;
        }

        .map-container {
          border-radius: 15px;
          overflow: hidden;
          border: 2px solid var(--public-border, #87CEEB);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .beach-popup h4 {
          margin: 0 0 0.5rem 0;
          color: #2C5F7F;
          font-size: 1.1rem;
        }

        .beach-popup p {
          margin: 0.25rem 0;
          font-size: 0.9rem;
          color: #666;
        }

        .beach-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .status-active {
          background-color: #28a745;
        }

        .floating-scroll-indicator {
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, var(--public-orange-start, #FF7F50), var(--public-orange-end, #FFB347));
          color: white;
          padding: 12px 24px;
          border-radius: 50px;
          box-shadow: 0 8px 24px rgba(255, 127, 80, 0.4);
          cursor: pointer;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          animation: float 3s ease-in-out infinite;
        }

        .floating-scroll-indicator:hover {
          transform: translateX(-50%) translateY(-3px);
          box-shadow: 0 12px 32px rgba(255, 127, 80, 0.6);
          background: linear-gradient(135deg, var(--public-orange-start, #FF6347), var(--public-orange-end, #FF8C69));
        }

        .floating-scroll-text {
          font-size: 14px;
          font-weight: 600;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
        }

        .floating-scroll-arrow {
          font-size: 16px;
          animation: bounce 2s infinite;
        }

        .floating-scroll-indicator:hover .floating-scroll-arrow {
          animation-duration: 1s;
        }

        @keyframes float {
          0%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(-5px);
          }
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-8px);
          }
          60% {
            transform: translateY(-4px);
          }
        }

        .schedule-intro {
          text-align: center;
          margin-bottom: 3rem;
          color: var(--public-primary, #2C5F7F);
        }

        .schedule-intro h2 {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.8);
        }

        .beaches-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .beach-card {
          background: linear-gradient(135deg, #FFFFFF 0%, var(--primary-light, #F0F8FF) 100%);
          border-radius: 20px;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border: 3px solid var(--public-border, #87CEEB);
        }

        .beach-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
        }

        .beach-card-header {
          background: linear-gradient(45deg, var(--public-accent, #20B2AA), #48D1CC);
          color: white;
          padding: 1.5rem;
          text-align: center;
        }

        .beach-name {
          font-size: 1.5rem;
          margin: 0 0 0.5rem 0;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
        }

        .beach-description {
          margin: 0;
          opacity: 0.9;
          font-size: 0.9rem;
        }

        .lifeguard-status {
          padding: 2rem 1.5rem;
          text-align: center;
        }

        .status-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .status-icon {
          font-size: 2.5rem;
          color: var(--public-accent, #20B2AA);
        }

        .status-text {
          font-size: 1.2rem;
          font-weight: bold;
          color: var(--public-primary, #2C5F7F);
        }

        .beach-card-footer {
          background: rgba(135, 206, 235, 0.2);
          padding: 1rem 1.5rem;
          text-align: center;
          color: var(--public-primary, #2C5F7F);
          font-weight: bold;
        }

        .beach-footer {
          background: linear-gradient(45deg, var(--public-primary, #2C5F7F), #4682B4);
          color: white;
          padding: 3rem 2rem 1rem;
          margin-top: 4rem;
        }

        .footer-content {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .safety-info h3,
        .emergency-info h3 {
          margin-bottom: 1rem;
          font-size: 1.3rem;
        }

        .safety-info ul {
          list-style: none;
          padding: 0;
        }

        .safety-info li {
          padding: 0.5rem 0;
          padding-left: 1.5rem;
          position: relative;
        }

        .safety-info li::before {
          content: '🏄‍♂️';
          position: absolute;
          left: 0;
        }

        .emergency-info p {
          margin: 0.5rem 0;
        }

        .footer-bottom {
          text-align: center;
          border-top: 1px solid rgba(255, 255, 255, 0.3);
          padding-top: 1rem;
        }

        .footer-bottom .copyright {
          margin: 0;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 0.95rem;
        }

        .footer-link {
          color: #ffffff;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border-bottom: 1px solid transparent;
        }

        .footer-link:hover {
          text-decoration: underline;
        }

        .footer-logo {
          height: 18px;
          width: auto;
        }

        .wave-footer {
          font-size: 2rem;
          margin-top: 1rem;
          animation: wave-motion 3s ease-in-out infinite;
        }

        @media (max-width: 768px) {
          .beach-title {
            font-size: 2rem;
          }

          .forecast-container {
            padding: 1rem;
          }

          .forecast-container h2 {
            font-size: 1.5rem;
          }

          .hourly-forecast {
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 0.75rem;
          }

          .hour-card {
            padding: 0.75rem;
          }

          .hour-temp {
            font-size: 1.1rem;
          }

          .daily-forecast {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .day-card {
            padding: 1rem;
          }

          .day-temps {
            gap: 0.75rem;
          }

          .day-high {
            font-size: 1.3rem;
          }

          .day-low {
            font-size: 1.1rem;
          }

          .day-details {
            grid-template-columns: repeat(3, 1fr);
            gap: 0.25rem;
          }

          .weather-widget {
            position: static;
            margin-top: 1rem;
            margin-left: auto;
            margin-right: auto;
            max-width: 200px;
          }

          .beaches-grid {
            grid-template-columns: 1fr;
          }



          .floating-scroll-indicator {
            bottom: 15px;
            padding: 10px 20px;
          }

          .floating-scroll-text {
            font-size: 12px;
          }

          .floating-scroll-arrow {
            font-size: 14px;
          }

          .schedule-content {
            padding: 1rem;
          }

          .beach-header {
            padding: 1.5rem 1rem;
          }

          .closure-content-fullscreen {
            margin: 5vh auto 1rem;
            padding: 2rem 1.5rem;
            max-width: 95%;
          }

          .closure-icon-large {
            font-size: 4rem;
          }

          .closure-title-large {
            font-size: 2.5rem;
            letter-spacing: 1px;
          }

          .closure-subtitle {
            font-size: 1.2rem;
          }

          .reason-text {
            font-size: 1.1rem;
          }

          .safety-warning {
            flex-direction: column;
            text-align: center;
            padding: 1.5rem;
          }

          .warning-icon {
            font-size: 2.5rem;
          }

          .closure-status {
            grid-template-columns: 1fr;
          }

          .lightning {
            font-size: 3rem;
          }

          .storm-clouds-main {
            font-size: 5rem;
          }

          .hourly-forecast-storm {
            grid-template-columns: repeat(3, 1fr);
            gap: 0.5rem;
          }

          .forecast-container-storm {
            padding: 1rem;
          }
        }

        /* Full-Screen Storm Closure Styles */
        .storm-closure-fullscreen {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 9999;
          overflow-y: auto;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #0f3460 70%, #1a1a2e 100%);
        }

        .storm-background-fullscreen {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background: 
            radial-gradient(circle at 20% 30%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 70%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
            linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #0f3460 70%, #1a1a2e 100%);
        }

        .lightning {
          position: absolute;
          font-size: 5rem;
          color: #FFD700;
          animation: lightning 3s infinite;
          text-shadow: 0 0 30px #FFD700, 0 0 60px #FFD700, 0 0 90px #FFD700;
          z-index: 1;
        }

        .lightning:nth-child(1) {
          top: 15%;
          left: 15%;
          animation-delay: 0s;
        }

        .lightning:nth-child(2) {
          top: 25%;
          right: 20%;
          animation-delay: 1s;
        }

        .lightning:nth-child(3) {
          top: 60%;
          left: 70%;
          animation-delay: 2s;
        }

        .lightning:nth-child(4) {
          top: 40%;
          left: 40%;
          animation-delay: 0.5s;
        }

        .lightning:nth-child(5) {
          top: 70%;
          right: 40%;
          animation-delay: 1.5s;
        }

        .storm-clouds-main {
          position: absolute;
          top: 10%;
          left: 50%;
          transform: translateX(-50%);
          font-size: 8rem;
          color: #708090;
          text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.7);
          animation: float 4s ease-in-out infinite;
          z-index: 2;
        }

        .rain-effect {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        .rain-drop {
          position: absolute;
          width: 2px;
          height: 20px;
          background: linear-gradient(to bottom, transparent, rgba(135, 206, 250, 0.6));
          animation: rain 1s linear infinite;
        }

        .rain-drop:nth-child(1) {
          left: 10%;
          animation-delay: 0s;
        }

        .rain-drop:nth-child(2) {
          left: 30%;
          animation-delay: 0.2s;
        }

        .rain-drop:nth-child(3) {
          left: 50%;
          animation-delay: 0.4s;
        }

        .rain-drop:nth-child(4) {
          left: 70%;
          animation-delay: 0.6s;
        }

        .rain-drop:nth-child(5) {
          left: 90%;
          animation-delay: 0.8s;
        }

        .closure-content-fullscreen {
          position: relative;
          z-index: 10;
          background: rgba(255, 255, 255, 0.95);
          margin: 15vh auto 2rem;
          max-width: 900px;
          border-radius: 20px;
          padding: 3rem;
          text-align: center;
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(10px);
        }

        .closure-icon-large {
          font-size: 6rem;
          margin-bottom: 1rem;
          display: block;
          animation: pulse 2s infinite;
          filter: drop-shadow(0 0 20px rgba(220, 20, 60, 0.5));
        }

        .closure-title-large {
          font-size: 4rem;
          color: #DC143C;
          margin-bottom: 0.5rem;
          font-weight: 900;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
          letter-spacing: 2px;
        }

        .closure-subtitle {
          font-size: 1.5rem;
          color: #2C5F7F;
          margin-bottom: 2rem;
          font-weight: 600;
        }

        .closure-reason-large {
          background: rgba(220, 20, 60, 0.1);
          border: 3px solid #DC143C;
          border-radius: 15px;
          padding: 1.5rem;
          margin: 2rem 0;
          box-shadow: 0 10px 20px rgba(220, 20, 60, 0.2);
        }

        .reason-label {
          font-size: 1.2rem;
          font-weight: bold;
          color: #DC143C;
          margin-bottom: 0.5rem;
        }

        .reason-text {
          font-size: 1.4rem;
          color: #DC143C;
          font-weight: 600;
        }

        .safety-warning {
          background: rgba(255, 165, 0, 0.1);
          border: 2px solid #FF8C00;
          border-radius: 15px;
          padding: 2rem;
          margin: 2rem 0;
          display: flex;
          align-items: flex-start;
          gap: 1rem;
        }

        .warning-icon {
          font-size: 3rem;
          animation: pulse 1.5s infinite;
        }

        .warning-text {
          text-align: left;
          color: #2C5F7F;
        }

        .warning-text p {
          margin: 0.5rem 0;
          font-size: 1.1rem;
          line-height: 1.6;
        }

        .closure-status {
          background: rgba(44, 95, 127, 0.1);
          border-radius: 15px;
          padding: 1.5rem;
          margin: 2rem 0;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }

        .status-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .status-label {
          font-size: 0.9rem;
          color: #2C5F7F;
          font-weight: 600;
        }

        .status-value {
          font-size: 1.1rem;
          color: #DC143C;
          font-weight: bold;
        }

        .reopening-notice {
          margin-top: 2rem;
          color: #2C5F7F;
        }

        .reopening-notice p {
          margin: 0.5rem 0;
          font-size: 1.1rem;
          font-weight: 500;
        }

        /* Storm Forecast Styles */
        .forecast-section-storm {
          position: relative;
          z-index: 10;
          margin: 2rem auto;
          max-width: 900px;
          padding: 0 1rem;
        }

        .forecast-container-storm {
          background: rgba(255, 255, 255, 0.9);
          border-radius: 15px;
          padding: 2rem;
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(5px);
        }

        .forecast-container-storm h3 {
          color: #2C5F7F;
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
          text-align: center;
        }

        .hourly-forecast-storm {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 1rem;
        }

        .hour-card-storm {
          background: rgba(240, 248, 255, 0.8);
          border: 2px solid rgba(176, 224, 230, 0.6);
          border-radius: 12px;
          padding: 1rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .hour-card-storm:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
        }

        .hour-details-storm {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          margin-top: 0.5rem;
          font-size: 0.8rem;
        }

        @keyframes lightning {
          0%, 90%, 100% {
            opacity: 0;
            transform: scale(1);
          }
          5%, 10% {
            opacity: 1;
            transform: scale(1.2);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        @keyframes rain {
          0% {
            top: -20px;
            opacity: 1;
          }
          100% {
            top: 100vh;
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default PublicBeachSchedule; 